import _ from 'underscore';

// @ts-expect-error
import * as UserActions from 'actions/UserActions';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import {ProjectSummary} from 'constants/SourceConstants';
import {BenchlingApp, User} from 'constants/UserConstants';
import SourceDAO from 'file-browser/SourceDAO';
import HomeDashboardActions from 'home-dashboard/HomeDashboardActions';
import AppDispatcher from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import * as FullScreenActions from 'nav/full-screen/FullScreenActions';
import notifications from 'notifications';
import * as OrganizationConstants from 'orgs/OrganizationConstants';
import {
  Organization,
  OrganizationPermission,
  OrganizationPermissionRole,
  Team,
} from 'orgs/OrganizationConstants';
import OrganizationDAO, {
  MembersTabInfo,
  OrganizationCreateData,
  OrganizationUpdateData,
} from 'orgs/OrganizationDAO';
import * as OrganizationUtil from 'orgs/OrganizationUtil';
import * as TeamDAO from 'orgs/TeamDAO';
import {CreateTeamData, UpdateTeamData} from 'orgs/TeamDAO';
import {TeamPageOrganization} from 'orgs/TeamPage';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {fetchTeamPageOrganization} from 'parties/organizations/OrganizationDAO';
import EntityStore from 'stores/EntityStore';
import stripExifData from 'util/stripExifData';

export function create({
  name,
  handle,
  labId,
  redirectOnSuccess,
}: {
  name: string;
  handle: string;
  labId?: number;
  redirectOnSuccess?: boolean;
}): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ORG_CREATE,
  });
  OrganizationDAO.create({name, handle, lab: labId})
    .done((response: Organization) => {
      if (redirectOnSuccess) {
        navigateToURL(`/organizations/${response.url_identifier}/settings`);
      }

      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_CREATE_COMPLETED,
        organization: response,
      });
    })
    .fail(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_CREATE_FAILED,
      });
    })
    .fail(notifications.notifyError);
}

export function createTeam(data: CreateTeamData): void {
  TeamDAO.create(data)
    .done((team: Team) => {
      navigateToURL(`/teams/${team.id}/settings`);
    })
    .fail(notifications.notifyError);
}

export function setInitials(
  orgId: string,
  {orgInitials, initialsByUserId}: {orgInitials?: string; initialsByUserId: {[userId: string]: string}},
  notify: boolean = true
): void {
  OrganizationDAO.setInitials(orgId, {orgInitials, initialsByUserId})
    .done(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_INITIALS_UPDATE_COMPLETED,
        orgId,
        orgInitials,
        initialsByUserId,
      });
      if (notify) {
        notifications.success('Initials updated');
      }
    })
    .fail((err) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_INITIALS_UPDATE_FAILED,
      });
      notifications.notifyError(err);
    });
}

export function search({query}: {query: string}): void {
  const searchTime = new Date().getTime();
  AppDispatcher.dispatch({
    actionType: EventConstants.ORG_SEARCH,
  });
  OrganizationDAO.search({query})
    .done((response: {organizations: Array<Organization>}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_SEARCH_COMPLETED,
        results: response.organizations,
        searchTime,
      });
    })
    .fail(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_SEARCH_FAILED,
      });
    })
    .fail(notifications.notifyError);
}

export function resetSearchOrgModalState(): void {
  AppDispatcher.dispatch({actionType: EventConstants.RESET_SEARCH_ORG_MODAL_STATE});
}

export async function fetchProjects(groupId: string): Promise<void> {
  const daoRequest = (): Promise<{projects: Array<ProjectSummary>}> => {
    if (OrganizationUtil.isTeam(groupId)) {
      return TeamDAO.fetchProjects(groupId);
    }
    return OrganizationDAO.fetchProjects(groupId);
  };

  let actionType;
  if (OrganizationUtil.isTeam(groupId)) {
    actionType = EventConstants.OPEN_TEAM_PROJECTS_TAB;
  } else {
    actionType = EventConstants.OPEN_ORGANIZATION_PROJECTS_TAB;
  }

  const {projects} = await daoRequest();
  AppDispatcher.dispatch({
    actionType,
    projects,
  });
}

export function fetchMembers(entityId: string): void {
  const daoRequest = (): JQueryPromise<{
    members: Array<User>;
    permissions: Array<OrganizationPermission>;
  }> => {
    if (OrganizationUtil.isTeam(entityId)) {
      return TeamDAO.getMembers(entityId, {getMemberInfo: false});
    }
    return OrganizationDAO.fetchMembers(entityId);
  };

  daoRequest()
    .done((response: {members: Array<User>; permissions: Array<OrganizationPermission>}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.GROUP_MEMBERS_FETCH_COMPLETED,
        members: response.members,
        permissions: response.permissions,
        entityId,
      });
    })
    .fail(notifications.notifyError);
}

export function fetchTeams(organizationId: string): void {
  OrganizationDAO.fetchTeams(organizationId)
    .done((response: {teams: Array<Team>}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.OPEN_ORGANIZATION_TEAMS_TAB,
        organizationTeams: response.teams,
      });
    })
    .fail(notifications.notifyError);
}

export function fetchInfoForMembersTab(entityId: string): void {
  const daoRequest = (): JQueryPromise<{
    members: Array<User>;
    permissions: Array<OrganizationPermission>;
  }> => {
    if (OrganizationUtil.isTeam(entityId)) {
      return TeamDAO.getMembers(entityId, {getMemberInfo: true});
    }
    // Fetch teams for members tab for user provisioning.
    fetchTeams(entityId);
    return OrganizationDAO.fetchInfoForMembersTab(entityId);
  };

  daoRequest()
    .done(({members, permissions, userIdToMemberInfo, invites, requests}: MembersTabInfo) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.OPEN_MEMBERS_TAB_COMPLETED,
        members,
        permissions,
        entityId,
        userIdToMemberInfo: _.mapObject(userIdToMemberInfo, (memberInfo) =>
          Object.assign(memberInfo, {loading: false})
        ),
        // Currently, only organizations support invites and requests, but these can be refactored for teams.
        organizationMemberInvites: invites || [],
        organizationMemberRequests: requests || [],
      });
    })
    .fail(notifications.notifyError);
}

export function cancelInvite(organizationId: string, email: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ORGANIZATION_CANCEL_INVITE,
    organizationId,
    email,
  });
  OrganizationDAO.cancelInvite(organizationId, email)
    .done(() => {
      notifications.success(`Canceled invite for ${email}`);
    })
    .fail(notifications.notifyError)
    .always(() => {
      fetchInfoForMembersTab(organizationId);
    });
}

export function updateActiveTab(entityId: string, activeTab: string): void {
  switch (activeTab) {
    case OrganizationConstants.CALENDAR_TAB: {
      fetchMembers(entityId);
      break;
    }
    case OrganizationConstants.PROJECTS_TAB: {
      fetchProjects(entityId);
      break;
    }
    case OrganizationConstants.MEMBERS_TAB: {
      fetchInfoForMembersTab(entityId);
      break;
    }
    case OrganizationConstants.APPS_TAB: {
      // Because Benchling Apps are Members under the hood, the same query is sufficient to render the tab
      fetchInfoForMembersTab(entityId);
      break;
    }
    case OrganizationConstants.TEAMS_TAB: {
      fetchTeams(entityId);
      break;
    }
    default: {
      break;
    }
  }

  AppDispatcher.dispatch({
    actionType: EventConstants.UPDATE_ACTIVE_TAB,
    activeTab,
  });
}

function _getActiveTab(
  section: string | null,
  urlParamToTabMap: {[key: string]: string},
  defaultTab: string = OrganizationConstants.PROJECTS_TAB
): string {
  const tabFromParam = section ? urlParamToTabMap[section] : null;
  return tabFromParam || defaultTab;
}

export function openOrganizationPage(organizationUrlIdentifier: string, section: string): void {
  FullScreenActions.showLoadingScreen();
  AppDispatcher.dispatch({actionType: EventConstants.OPEN_ORGANIZATION_PAGE});

  OrganizationDAO.get(organizationUrlIdentifier)
    .done((organization: Organization) => {
      const activeTab = _getActiveTab(
        section,
        OrganizationConstants.ORGANIZATION_PAGE_SECTION_URL_PARAM_TO_TAB
      );
      AppDispatcher.dispatch({
        actionType: EventConstants.OPEN_ORGANIZATION_PAGE_COMPLETED,
        organization,
        activeTab,
      });
      FullScreenActions.showScreen(OrganizationConstants.ORGANIZATION_SCREEN);
      updateActiveTab(organization.id, activeTab);
    })
    .fail(() => {
      notifications.error('There was an error loading the organization page');
      AppDispatcher.dispatch({actionType: EventConstants.OPEN_ORGANIZATION_PAGE_FAILED});
      FullScreenActions.hideScreen();
    });
}

export async function openTeamPage(teamId: string, section: string): Promise<void> {
  async function completeOpenTeamPage(team: Team): Promise<void> {
    const entityOrganization = EntityStore.getEntity(team.organizationId);
    let organization: TeamPageOrganization;
    if (entityOrganization == null) {
      organization = await fetchTeamPageOrganization(team.organizationId);
    } else {
      organization = entityOrganization as TeamPageOrganization;
    }
    assert(organization != null, 'Organization not found.');
    const activeTab = _getActiveTab(section, OrganizationConstants.TEAM_PAGE_SECTION_URL_PARAM_TO_TAB);
    AppDispatcher.dispatch({
      actionType: EventConstants.OPEN_TEAM_PAGE_COMPLETED,
      team,
      organization,
      activeTab,
    });
    FullScreenActions.showScreen(OrganizationConstants.TEAM_SCREEN);
    updateActiveTab(team.id, activeTab);
  }

  FullScreenActions.showLoadingScreen();
  AppDispatcher.dispatch({actionType: EventConstants.OPEN_TEAM_PAGE});

  // Check EntityStore for the team to avoid refetching.
  const entityStoreTeam = EntityStore.getEntity(teamId) as Team;
  if (entityStoreTeam) {
    await completeOpenTeamPage(entityStoreTeam);
    return;
  }

  TeamDAO.get(teamId)
    .done((response) => {
      completeOpenTeamPage(response as Team);
    })
    .fail(() => {
      notifications.error('There was an error loading the team page.');
      AppDispatcher.dispatch({actionType: EventConstants.OPEN_TEAM_PAGE_FAILED});
      FullScreenActions.hideScreen();
    });
}

export function openUserPage(userId: string, section: string | null): void {
  const activeTab = _getActiveTab(
    section,
    OrganizationConstants.USER_PAGE_SECTION_URL_PARAM_TO_TAB,
    OrganizationConstants.SETTINGS_TAB
  );
  AppDispatcher.dispatch({
    actionType: EventConstants.OPEN_USER_PAGE_COMPLETED,
    userId,
    activeTab,
  });
  FullScreenActions.showScreen(OrganizationConstants.USER_SCREEN);
}

export function inviteUser(organizationId: string, emailOrUrlId: string): void {
  const organization = EntityStore.getEntity(organizationId)!;
  OrganizationDAO.inviteUser(organizationId, emailOrUrlId)
    .done(() => {
      UserActions.getReferrals();
      notifications.success(`Invited ${emailOrUrlId} to join ${organization.name}`);
      fetchInfoForMembersTab(organizationId);
    })
    .fail(notifications.notifyError);
}

export async function addMember(organizationId: string, user: User | BenchlingApp): Promise<void> {
  await OrganizationDAO.addMember(organizationId, user.id);
  notifications.success(`Added ${getPartyDisplayString(user)} to organization`);
  fetchInfoForMembersTab(organizationId);
}

export function addTeamMember(teamId: string, user: User | BenchlingApp): void {
  TeamDAO.addMember(teamId, user.id)
    .done(() => {
      const team = EntityStore.getEntity(teamId)!;
      notifications.success(`Added ${getPartyDisplayString(user)} to ${team.name}`);
      fetchInfoForMembersTab(teamId);
    })
    .fail(notifications.notifyError);
}

export function acceptInboundRequest(organizationId: string, userId: string): void {
  const user = EntityStore.getEntity(userId)!;
  const organization = EntityStore.getEntity(organizationId)!;
  AppDispatcher.dispatch({
    actionType: EventConstants.ORG_ACCEPT_REQUEST,
    organizationId,
    userId,
  });
  OrganizationDAO.acceptInboundRequest(organizationId, userId)
    .done(() => {
      notifications.success(`Added ${user.name} to ${organization.name}.`);
      fetchInfoForMembersTab(organizationId);
    })
    .fail(notifications.notifyError);
}

export function rejectInboundRequest(organizationId: string, userId: string): void {
  const user = EntityStore.getEntity(userId)!;
  const organization = EntityStore.getEntity(organizationId)!;
  AppDispatcher.dispatch({
    actionType: EventConstants.ORG_REJECT_REQUEST,
    organizationId,
    userId,
  });
  OrganizationDAO.rejectInboundRequest(organizationId, userId)
    .done(() => {
      notifications.success(`Rejected ${user.name}'s request to join ${organization.name}.`);
      fetchInfoForMembersTab(organizationId);
    })
    .fail(notifications.notifyError);
}

export function openCreateOrganizationPage(organization: {name: string}): void {
  FullScreenActions.showLoadingScreen();
  AppDispatcher.dispatch({
    actionType: EventConstants.OPEN_CREATE_ORGANIZATION_PAGE,
    organization,
  });
  HomeDashboardActions.closeSearchOrgModal();
  FullScreenActions.showScreen(OrganizationConstants.CREATE_ORGANIZATION_SCREEN);
}

export function openCreateTeamPage(organizationUrlIdentifier: string): void {
  FullScreenActions.showLoadingScreen();

  OrganizationDAO.get(organizationUrlIdentifier)
    .done((organization) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.OPEN_CREATE_TEAM_PAGE,
        organization,
      });
      FullScreenActions.showScreen(OrganizationConstants.CREATE_TEAM_SCREEN);
    })
    .fail((response) => {
      notifications.error('There was an error loading the create team page.');
      AppDispatcher.dispatch({actionType: EventConstants.OPEN_CREATE_TEAM_PAGE_FAILED});
      navigateToURL('/editor');
      FullScreenActions.hideScreen();
    });
}

export function createWithInfo(
  organization: OrganizationCreateData & {avatar?: File | null},
  usersToInvite: Array<string>,
  emailsToInvite: Array<string>
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ORG_CREATE,
  });
  Promise.resolve(OrganizationDAO.create(organization))
    .then((org: Organization) => {
      if (organization.avatar) {
        return Promise.resolve(OrganizationDAO.updateOrganizationAvatar(org.id, organization.avatar)).then(
          () => org
        );
      }
      return org;
    })
    .then((org) => {
      if (usersToInvite.length || emailsToInvite.length) {
        OrganizationDAO.inviteUsers(org.id, usersToInvite, emailsToInvite);
      }
      return org;
    })
    .then((response) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_CREATE_COMPLETED,
        organization: response,
      });
      FullScreenActions.showScreen(OrganizationConstants.SHARE_PROJECTS_SCREEN);
    })
    .catch((error) => {
      notifications.notifyError(error);
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_CREATE_FAILED,
      });
    });
}

export function shareProjectsWithOrganization(
  sourceIds: Array<string>,
  dataPolicyIds: Array<string>,
  organizationId: string
): void {
  const onShareProjectsCompleted = (): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ORGANIZATION_SHARE_PROJECTS_COMPLETED,
    });
    FullScreenActions.showScreen(OrganizationConstants.ORGANIZATION_SCREEN);
    updateActiveTab(organizationId, OrganizationConstants.PROJECTS_TAB);
  };

  if (_.isEmpty(sourceIds)) {
    AppDispatcher.dispatch({
      actionType: EventConstants.ORGANIZATION_SHARE_PROJECTS,
    });
    onShareProjectsCompleted();
  } else {
    callWithDataModifications({
      changeSummary: `${sourceIds.length} ${pluralize(sourceIds.length, 'project')} will be modified`,
      onPerformAction: (dataModsHeader?: DataModificationsHeader) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.ORGANIZATION_SHARE_PROJECTS,
        });
        SourceDAO.setCollaboratorForSources(
          {
            entityId: organizationId,
            sourceIds,
            dataPolicyIds,
          },
          dataModsHeader
        )
          .then(onShareProjectsCompleted)
          .catch((error: Response) => {
            notifications.notifyError(error);
            AppDispatcher.dispatch({
              actionType: EventConstants.ORGANIZATION_SHARE_PROJECTS_FAILED,
            });
          });
      },
      apiIds: sourceIds,
      analyticsParams: {
        module: 'PROJECT_FOLDER',
        // @ts-expect-error TODO: Should be using ActionOrigin type
        actionOrigin: 'organization_create_flow',
        modifiedAction: 'MODIFY',
        numModifiedTotalItems: sourceIds.length,
        entryId: null,
      },
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_PROJECTS_FOLDERS,
    });
  }
}

export function update(organizationId: string, data: OrganizationUpdateData): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ORG_UPDATE,
    entityId: organizationId,
  });

  OrganizationDAO.update(organizationId, data)
    .then((organization) => {
      notifications.success('Organization updated successfully.');
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_UPDATE_COMPLETED,
        organization,
      });
    })
    .catch((error) => {
      notifications.notifyError(error);
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_UPDATE_FAILED,
        entityId: organizationId,
      });
    });
}

export function updateTeam(teamId: string, data: UpdateTeamData): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.TEAM_UPDATE,
    entityId: teamId,
  });

  TeamDAO.update(teamId, data)
    .then((team) => {
      notifications.success('Team updated successfully.');
      AppDispatcher.dispatch({
        actionType: EventConstants.TEAM_UPDATE_COMPLETED,
        team,
      });
    })
    .catch((error: Response) => {
      notifications.notifyError(error);
      AppDispatcher.dispatch({
        actionType: EventConstants.TEAM_UPDATE_FAILED,
        entityId: teamId,
      });
    });
}

export async function tombstoneOrganization(organizationId: string): Promise<void> {
  await OrganizationDAO.tombstone(organizationId);
  notifications.success('Deleted organization successfully.');
  // This isn't very elegant, but it ensures that all state is reset cleanly
  // and is an infrequent action.
  window.location.href = '/editor';
}

export function deleteOrganization(organizationId: string): void {
  // Deprecated in favor of tombstoneOrganization
  OrganizationDAO.delete(organizationId)
    .then(() => {
      notifications.success('Deleted organization successfully.');
      // This isn't very elegant, but it ensures that all state is reset cleanly
      // and is an infrequent action.
      window.location.href = '/editor';
    })
    .catch(notifications.notifyError);
}

export function updateOrganizationAvatar(organizationId: string, avatarFile: File): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ORG_UPDATE,
  });

  stripExifData(avatarFile)
    .then((newAvatarFile) => OrganizationDAO.updateOrganizationAvatar(organizationId, newAvatarFile))
    .then((organization) => {
      notifications.success('Avatar updated successfully.');
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_UPDATE_COMPLETED,
        organization,
      });
    })
    .catch((error) => {
      notifications.notifyError(error);
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_UPDATE_FAILED,
      });
    });
}

export function removeOrganizationAvatar(organizationId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ORG_UPDATE,
  });

  OrganizationDAO.removeOrganizationAvatar(organizationId)
    .then((organization) => {
      notifications.success('Avatar removed.');
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_UPDATE_COMPLETED,
        organization,
      });
    })
    .catch((error) => {
      notifications.notifyError(error);
      AppDispatcher.dispatch({
        actionType: EventConstants.ORG_UPDATE_FAILED,
      });
    });
}

export function updateTeamAvatar(teamId: string, avatarFile: File): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.TEAM_UPDATE,
  });

  stripExifData(avatarFile)
    .then((newAvatarFile) => TeamDAO.updateTeamAvatar(teamId, newAvatarFile))
    .then((team) => {
      notifications.success('Avatar updated successfully.');
      AppDispatcher.dispatch({
        actionType: EventConstants.TEAM_UPDATE_COMPLETED,
        team,
      });
    })
    .catch((error: Response) => {
      notifications.notifyError(error);
      AppDispatcher.dispatch({
        actionType: EventConstants.TEAM_UPDATE_FAILED,
      });
    });
}

export function removeTeamAvatar(teamId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.TEAM_UPDATE,
  });

  TeamDAO.removeTeamAvatar(teamId)
    .then((team) => {
      notifications.success('Avatar removed.');
      AppDispatcher.dispatch({
        actionType: EventConstants.TEAM_UPDATE_COMPLETED,
        team,
      });
    })
    .catch((error: Response) => {
      notifications.notifyError(error);
      AppDispatcher.dispatch({
        actionType: EventConstants.TEAM_UPDATE_FAILED,
      });
    });
}

export function updateMemberPermission(
  entityId: string,
  userId: string,
  role: OrganizationPermissionRole
): void {
  const daoRequest = (): JQueryPromise<{}> => {
    if (OrganizationUtil.isTeam(entityId)) {
      return TeamDAO.updateMemberRole(entityId, userId, {role});
    }
    return OrganizationDAO.updateOrganizationPermission(entityId, userId, {memberLevel: role});
  };

  AppDispatcher.dispatch({
    actionType: EventConstants.MEMBER_PERMISSION_UPDATE,
    userId,
  });

  daoRequest()
    .done(() => {
      notifications.success('Permissions updated successfully.');
      AppDispatcher.dispatch({
        actionType: EventConstants.MEMBER_PERMISSION_UPDATE_COMPLETED,
        entityId,
        userId,
        role,
      });
    })
    .fail((error) => {
      notifications.notifyError(error);
      AppDispatcher.dispatch({
        actionType: EventConstants.MEMBER_PERMISSION_UPDATE_FAILED,
        userId,
      });
    });
}

export function removeMember(entityId: string, userId: string): void {
  const isTeamMember = OrganizationUtil.isTeam(entityId);
  const daoRequest = isTeamMember
    ? TeamDAO.removeMember(entityId, userId)
    : OrganizationDAO.removeMember(entityId, userId);
  AppDispatcher.dispatch({
    actionType: EventConstants.MEMBER_REMOVAL,
    userId,
  });

  daoRequest
    .done(() => {
      notifications.success(`Successfully removed ${isTeamMember ? 'team' : 'organization'} member.`);
      AppDispatcher.dispatch({
        actionType: EventConstants.MEMBER_REMOVAL_COMPLETED,
        entityId,
        userId,
      });
    })
    .fail((error: Response) => {
      notifications.notifyError(error);
      AppDispatcher.dispatch({
        actionType: EventConstants.MEMBER_REMOVAL_FAILED,
        userId,
      });
    });
}

export function deleteTeam(teamId: string): void {
  TeamDAO.deleteTeam(teamId)
    .then(() => {
      notifications.success('Deleted team successfully.');
      window.location.href = '/editor';
    })
    .catch(notifications.notifyError);
}
